import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ThemeConfig {
  primaryHue: number;
  primarySaturation: number;
  primaryLightness: number;
  secondaryHue: number;
  secondarySaturation: number;
  secondaryLightness: number;
  accentHue: number;
  accentSaturation: number;
  accentLightness: number;
  borderRadius: number;
  fontHeading: string;
  fontBody: string;
}

let themeApplied = false;

/**
 * Fetches theme settings from platform_settings and applies them as CSS variables.
 * Called once at app level to apply admin-configured theme.
 */
export function useAdminTheme() {
  const [isLoaded, setIsLoaded] = useState(themeApplied);

  useEffect(() => {
    if (themeApplied) {
      setIsLoaded(true);
      return;
    }

    const applyTheme = async () => {
      try {
        const { data, error } = await supabase
          .from("platform_settings")
          .select("setting_key, setting_value")
          .eq("category", "theme");

        if (error) throw error;

        const settings: Record<string, string> = {};
        (data || []).forEach((item) => {
          settings[item.setting_key] = item.setting_value;
        });

        const root = document.documentElement;

        // Primary color
        const pH = settings["theme_primary_hue"];
        const pS = settings["theme_primary_saturation"];
        const pL = settings["theme_primary_lightness"];
        if (pH && pS && pL) {
          root.style.setProperty("--primary", `${pH} ${pS}% ${pL}%`);
          root.style.setProperty("--primary-glow", `${pH} ${pS}% ${Math.min(100, parseInt(pL) + 10)}%`);
          root.style.setProperty("--ring", `${pH} ${pS}% ${Math.min(100, parseInt(pL) + 15)}%`);
        }

        // Secondary color
        const sH = settings["theme_secondary_hue"];
        const sS = settings["theme_secondary_saturation"];
        const sL = settings["theme_secondary_lightness"];
        if (sH && sS && sL) {
          root.style.setProperty("--secondary", `${sH} ${sS}% ${sL}%`);
          root.style.setProperty("--secondary-variant", `${Math.max(0, parseInt(sH) - 9)} ${Math.min(100, parseInt(sS) + 6)}% ${Math.min(100, parseInt(sL) + 2)}%`);
        }

        // Accent color
        const aH = settings["theme_accent_hue"];
        const aS = settings["theme_accent_saturation"];
        const aL = settings["theme_accent_lightness"];
        if (aH && aS && aL) {
          root.style.setProperty("--accent", `${aH} ${aS}% ${aL}%`);
          root.style.setProperty("--accent-variant", `${Math.max(0, parseInt(aH) - 10)} ${Math.min(100, parseInt(aS) - 5)}% ${Math.min(100, parseInt(aL) - 5)}%`);
        }

        // Border radius
        const radius = settings["theme_border_radius"];
        if (radius) {
          root.style.setProperty("--radius", `${radius}rem`);
        }

        // Fonts
        const fontHeading = settings["theme_font_heading"];
        const fontBody = settings["theme_font_body"];
        if (fontBody && fontBody !== "Inter") {
          // Load the font from Google Fonts
          loadGoogleFont(fontBody);
          root.style.setProperty("--font-body", `'${fontBody}', sans-serif`);
          document.body.style.fontFamily = `'${fontBody}', sans-serif`;
        }
        if (fontHeading && fontHeading !== "Inter") {
          loadGoogleFont(fontHeading);
          root.style.setProperty("--font-heading", `'${fontHeading}', sans-serif`);
        }

        // Update gradients
        if (pH && pS && pL && sH && sS && sL && aH && aS && aL) {
          const pGlow = `${pH} ${pS}% ${Math.min(100, parseInt(pL) + 15)}%`;
          const pVar = `${Math.min(360, parseInt(pH) + 8)} 84% ${Math.min(100, parseInt(pL) + 10)}%`;
          root.style.setProperty(
            "--gradient-primary",
            `linear-gradient(135deg, hsl(${pGlow}) 0%, hsl(${pVar}) 50%, hsl(${sH} ${sS}% ${sL}%) 100%)`
          );
          root.style.setProperty(
            "--gradient-hero",
            `linear-gradient(135deg, hsl(${pH} ${pS}% ${Math.min(100, parseInt(pL) + 10)}%) 0%, hsl(${pVar}) 50%, hsl(${sH} ${sS}% ${Math.min(100, parseInt(sL) + 7)}%) 100%)`
          );
        }

        themeApplied = true;
      } catch (err) {
        console.error("Failed to apply admin theme:", err);
      } finally {
        setIsLoaded(true);
      }
    };

    applyTheme();
  }, []);

  return { isLoaded };
}

function loadGoogleFont(fontName: string) {
  const existing = document.querySelector(`link[data-font="${fontName}"]`);
  if (existing) return;

  const link = document.createElement("link");
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, "+")}:wght@300;400;500;600;700&display=swap`;
  link.rel = "stylesheet";
  link.dataset.font = fontName;
  document.head.appendChild(link);
}
